import isValidUrl from './helpers/is-valid-url.js';
import getFallbackId from './helpers/get-fallback-id.js';
import getHost from './helpers/get-host.js';
import getPathId from './helpers/get-path-id.js';
import getQueryId from './helpers/get-query-id.js';
import getHashId from './helpers/get-hash-id.js';
import urlParser from './helpers/parse-url.js';
import { isInternalUrl, findLabradorId } from './helpers/is-internal-url.js';
import { findAllerId } from './helpers/get-aller-id.js';

/**
 * Takes an URL and returns an ID.
 * @param {string | URL} url
 * @param {boolean} useAllerId Use AllerID query param to override other Ids. true by default for backward compatibility.
 * @returns {string}
 */
export default function getExternalId(url, useAllerId = true) {
	if (!url || url === '') {
		throw new Error('No URL provided');
	}

	const parsedUrl = urlParser(url);

	if (!parsedUrl) {
		throw new Error('Not a valid URL format for generating ID');
	}

	const { hostname, searchParams, pathname, hash, protocol } = parsedUrl;
	const host = getHost(hostname);
	const hasQueryParams = Array.from(searchParams).length > 0;

	if (hasQueryParams && useAllerId) {
		// prioritise commercial department's AllerID query param if set
		// it is a hack they've been using for years to use this library
		// combined with blink to get tracking for commercial content.
		// adding this here allows this library to generate external-ids for
		// front pages when AllerID is set, which changes the previous requirement
		// of `isValidUrl` to only consider articles
		const allerId = findAllerId(searchParams);
		if (allerId) {
			return `${host}/${allerId}`;
		}
	}

	// early return for our own sites when we find a labradorid in path
	// solves some problems with other sites' tracking query params
	// overriding the returned id we expect
	if (pathname !== '' && isInternalUrl(host)) {
		const labradorID = findLabradorId(pathname);

		if (labradorID) {
			return `${host}/${labradorID}`;
		}
	}

	if (!isValidUrl(parsedUrl)) {
		throw new Error('Not a valid URL format for generating ID');
	}

	let id = '';

	// Has query params, check for ID
	if (hasQueryParams) {
		id = getQueryId(host, searchParams) || '';
	}

	// Has hash, check for ID
	if (!id && hash) {
		id = getHashId(host, hash);
	}

	// Check for ID in path
	if (!id) {
		id = getPathId(host, pathname);
	}

	// Return the ID if we found one.
	if (id) {
		return id;
	}

	// Remove query params and hash if present.
	const cleanUrl = `${protocol}//${hostname}${pathname}`;

	// Use fallback ID when no ID is present in URL
	return getFallbackId(host, cleanUrl);
}
